import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Tabs, Tab, Paper, Button, IconButton,
  Chip, CircularProgress, Alert, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, Select, MenuItem,
  InputLabel, FormControl, Table, TableBody, TableCell,
  TableHead, TableRow, Collapse
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  KeyboardArrowUp as UpIcon,
  KeyboardArrowDown as DownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as RequiredIcon,
  Schedule as TimeIcon,
} from '@mui/icons-material';
import axios from 'axios';
import AdminPageLayout from '../components/admin/AdminPageLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

const JOB_TYPES = ['RESIDENTIAL', 'VACATION_RENTAL', 'COMMERCIAL'];

const SERVICE_TYPE_LABELS = {
  null:              'All (Default)',
  regular:           'Regular / Recurring',
  deep_clean:        'Deep Clean',
  move_in:           'Move In',
  move_out:          'Move Out',
  one_time:          'One Time',
  post_construction: 'Post Construction',
};

const SERVICE_TYPES = Object.keys(SERVICE_TYPE_LABELS);

const EMPTY_FORM = {
  title: '',
  category: '',
  description: '',
  estimatedMinutes: '',
  isRequired: true,
  serviceType: '',
  jobType: 'RESIDENTIAL',
};

// ── Task Edit/Add Dialog ──────────────────────────────────────────────────────
const TaskDialog = ({ open, onClose, onSave, initial, jobType, defaultServiceType }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(initial
        ? { ...initial, estimatedMinutes: initial.estimatedMinutes ?? '', serviceType: initial.serviceType ?? '' }
        : { ...EMPTY_FORM, jobType, serviceType: defaultServiceType ?? '' }
      );
      setError('');
    }
  }, [open, initial, jobType, defaultServiceType]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.title.trim() || !form.category.trim()) {
      setError('Title and category are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        ...form,
        jobType,
        estimatedMinutes: form.estimatedMinutes ? parseInt(form.estimatedMinutes) : null,
        serviceType: form.serviceType || null,
      });
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{initial ? 'Edit Task' : 'Add Task'}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}

        <TextField label="Title *" value={form.title} onChange={set('title')} fullWidth size="small" autoFocus />
        <TextField label="Category *" value={form.category} onChange={set('category')} fullWidth size="small"
          helperText="e.g. General, Bedrooms, Bathrooms, Kitchen, Laundry" />
        <TextField label="Description" value={form.description} onChange={set('description')} fullWidth size="small" multiline minRows={2} />

        <Box display="flex" gap={2}>
          <TextField label="Est. minutes" value={form.estimatedMinutes} onChange={set('estimatedMinutes')}
            type="number" size="small" sx={{ width: 140 }} inputProps={{ min: 1, max: 480 }} />
          <FormControl size="small" fullWidth>
            <InputLabel>Service type</InputLabel>
            <Select value={form.serviceType} label="Service type" onChange={set('serviceType')}>
              {SERVICE_TYPES.map(st => (
                <MenuItem key={st} value={st === 'null' ? '' : st}>
                  {SERVICE_TYPE_LABELS[st === 'null' ? 'null' : st]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <FormControlLabel
          control={<Switch checked={form.isRequired} onChange={e => setForm(f => ({ ...f, isRequired: e.target.checked }))} />}
          label="Required task"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : null}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Confirm Delete Dialog ─────────────────────────────────────────────────────
const DeleteDialog = ({ open, onClose, onConfirm, title }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs">
    <DialogTitle>Delete task?</DialogTitle>
    <DialogContent>
      <Typography>Remove <strong>{title}</strong> from this checklist? This cannot be undone.</Typography>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" color="error" onClick={onConfirm}>Delete</Button>
    </DialogActions>
  </Dialog>
);

// ── Category Group ────────────────────────────────────────────────────────────
const CategoryGroup = ({ category, tasks, onEdit, onDelete, onMove }) => {
  const [open, setOpen] = useState(true);

  return (
    <Box mb={2}>
      <Box
        display="flex" alignItems="center" gap={1} px={1} py={0.5}
        sx={{ cursor: 'pointer', bgcolor: 'grey.100', borderRadius: 1 }}
        onClick={() => setOpen(v => !v)}
      >
        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1, textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
          {category}
        </Typography>
        <Typography variant="caption" color="text.secondary">{tasks.length} tasks</Typography>
      </Box>

      <Collapse in={open}>
        <Table size="small" sx={{ mt: 0.5 }}>
          <TableHead>
            <TableRow sx={{ '& th': { py: 0.5, color: 'text.secondary', fontSize: 11 } }}>
              <TableCell>Task</TableCell>
              <TableCell align="center" width={70}>Min</TableCell>
              <TableCell align="center" width={70}>Req.</TableCell>
              <TableCell align="center" width={110}>Order</TableCell>
              <TableCell align="right" width={80}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((t, idx) => (
              <TableRow key={t.id} hover>
                <TableCell>
                  <Typography variant="body2">{t.title}</Typography>
                  {t.description && <Typography variant="caption" color="text.secondary">{t.description}</Typography>}
                </TableCell>
                <TableCell align="center">
                  {t.estimatedMinutes ? (
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.3}>
                      <TimeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                      <Typography variant="caption">{t.estimatedMinutes}</Typography>
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell align="center">
                  <RequiredIcon sx={{ fontSize: 16, color: t.isRequired ? 'success.main' : 'disabled' }} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Move up">
                    <span><IconButton size="small" onClick={() => onMove(t, idx, -1)} disabled={idx === 0}>
                      <UpIcon fontSize="small" />
                    </IconButton></span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span><IconButton size="small" onClick={() => onMove(t, idx, 1)} disabled={idx === tasks.length - 1}>
                      <DownIcon fontSize="small" />
                    </IconButton></span>
                  </Tooltip>
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => onEdit(t)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => onDelete(t)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Collapse>
    </Box>
  );
};

// ── Service Type Panel ────────────────────────────────────────────────────────
const ServiceTypePanel = ({ serviceType, templates, jobType, onAdd, onEdit, onDelete, onMove }) => {
  const label = SERVICE_TYPE_LABELS[serviceType ?? 'null'] || serviceType;

  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle1" fontWeight={600}>{label}</Typography>
          <Chip label={`${templates.length} tasks`} size="small" variant="outlined" />
        </Box>
        <Button size="small" startIcon={<AddIcon />} variant="outlined"
          onClick={() => onAdd(serviceType)}>
          Add task
        </Button>
      </Box>

      <Divider sx={{ mb: 1.5 }} />

      {templates.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No tasks yet. Click "Add task" to create the first one.
        </Typography>
      ) : (
        Object.entries(grouped).map(([cat, tasks]) => (
          <CategoryGroup
            key={cat}
            category={cat}
            tasks={tasks}
            onEdit={onEdit}
            onDelete={onDelete}
            onMove={(task, idx, dir) => onMove(task, tasks, dir)}
          />
        ))
      )}
    </Paper>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────
const TaskTemplatesPage = () => {
  const [jobTypeTab, setJobTypeTab] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [defaultServiceType, setDefaultServiceType] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentJobType = JOB_TYPES[jobTypeTab];

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/task-templates?includeInactive=true`, {
        headers: authHeaders()
      });
      setTemplates(res.data);
    } catch {
      setError('Failed to load task templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Templates for current jobType tab
  const visibleTemplates = templates.filter(t => t.jobType === currentJobType);

  // Group by serviceType (null = default)
  const byServiceType = visibleTemplates.reduce((acc, t) => {
    const key = t.serviceType ?? '__null__';
    if (!acc[key]) acc[key] = [];
    acc[key].push(t);
    return acc;
  }, {});

  // Always show at least the default (null) group
  const serviceTypeGroups = ['__null__', ...SERVICE_TYPES.filter(s => s !== 'null' && byServiceType[s])];
  const uniqueGroups = [...new Set(serviceTypeGroups)];

  const handleAdd = (serviceType) => {
    setEditingTemplate(null);
    setDefaultServiceType(serviceType === '__null__' ? null : serviceType);
    setDialogOpen(true);
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setDefaultServiceType(template.serviceType);
    setDialogOpen(true);
  };

  const handleSave = async (data) => {
    if (editingTemplate) {
      await axios.put(`${API_URL}/api/task-templates/${editingTemplate.id}`, data, { headers: authHeaders() });
    } else {
      await axios.post(`${API_URL}/api/task-templates`, data, { headers: authHeaders() });
    }
    await fetchTemplates();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await axios.delete(`${API_URL}/api/task-templates/${deleteTarget.id}`, { headers: authHeaders() });
    setDeleteTarget(null);
    await fetchTemplates();
  };

  const handleMove = async (task, siblings, dir) => {
    const idx = siblings.findIndex(t => t.id === task.id);
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const a = siblings[idx];
    const b = siblings[swapIdx];
    const payload = [
      { id: a.id, orderIndex: b.orderIndex },
      { id: b.id, orderIndex: a.orderIndex }
    ];
    await axios.patch(`${API_URL}/api/task-templates/reorder`, payload, { headers: authHeaders() });
    await fetchTemplates();
  };

  return (
    <AdminPageLayout maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Task Templates</Typography>
          <Typography variant="body2" color="text.secondary">
            Define the default checklist for each job type and service type.
            These tasks are automatically created when a job is imported or created.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleAdd(null)}>
          Add task
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Job type tabs */}
      <Tabs value={jobTypeTab} onChange={(_, v) => setJobTypeTab(v)} sx={{ mb: 3 }}
        TabIndicatorProps={{ style: { height: 3 } }}>
        {JOB_TYPES.map((jt, i) => {
          const count = templates.filter(t => t.jobType === jt).length;
          return (
            <Tab key={jt} label={
              <Box display="flex" alignItems="center" gap={0.8}>
                {jt.replace(/_/g, ' ')}
                <Chip label={count} size="small" color={jobTypeTab === i ? 'primary' : 'default'} />
              </Box>
            } />
          );
        })}
      </Tabs>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          {uniqueGroups.map(key => {
            const st = key === '__null__' ? null : key;
            const groupTemplates = (byServiceType[key] || []).slice().sort((a, b) => a.orderIndex - b.orderIndex);
            return (
              <ServiceTypePanel
                key={key}
                serviceType={st}
                templates={groupTemplates}
                jobType={currentJobType}
                onAdd={handleAdd}
                onEdit={handleEdit}
                onDelete={setDeleteTarget}
                onMove={handleMove}
              />
            );
          })}

          {/* Button to add a new service type section */}
          <Box mt={1}>
            <Typography variant="caption" color="text.secondary">
              Add tasks under a specific service type to override the default checklist for that cleaning type.
            </Typography>
          </Box>
        </>
      )}

      <TaskDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        initial={editingTemplate}
        jobType={currentJobType}
        defaultServiceType={defaultServiceType}
      />

      <DeleteDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={deleteTarget?.title}
      />
    </AdminPageLayout>
  );
};

export default TaskTemplatesPage;
